import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Upload, Loader2, Play, X, RefreshCw, Clock, Film, ChevronDown, Check, Maximize2, Plus, Volume2, VolumeX, Sliders } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

async function pollTask(taskId, token) {
  const maxPolls = 600;
  let notFoundCount = 0;
  for (let i = 0; i < maxPolls; i++) {
    await new Promise(r => setTimeout(r, 3000));
    try {
      const { data } = await axios.get(`${API}/api/recipes/happyhorse-direct/task/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (data.status === 'done') return data;
      if (data.status === 'failed') throw new Error(data.error || 'Generation failed');
      notFoundCount = 0;
    } catch (err) {
      if (err.response?.status === 404) {
        notFoundCount++;
        if (notFoundCount > 30) throw new Error('Task lost');
      } else if (err.message && !err.response) {
        throw err;
      }
    }
  }
  throw new Error('Generation timed out');
}

export default function HappyHorseDirectInputs({ token, onVideoStateChange, restoredData }) {
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imageOrientation, setImageOrientation] = useState('landscape');

  const [prompt, setPrompt] = useState('');
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [duration, setDuration] = useState(5);
  const [sound, setSound] = useState(false);
  const [cfgScale, setCfgScale] = useState(0.5);

  // Elements (up to 3 character/object references)
  const [elements, setElements] = useState([]); // [{file, preview, url, label}]

  const [generating, setGenerating] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const [runs, setRuns] = useState([]);
  const [runsOpen, setRunsOpen] = useState(true);

  const fileInputRef = useRef(null);
  const elementInputRef = useRef(null);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/api/recipes/happyhorse-direct/runs`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(({ data }) => setRuns(data.runs || [])).catch(() => {});
  }, [token]);

  useEffect(() => {
    if (!restoredData) return;
    if (restoredData.prompt) setPrompt(restoredData.prompt);
    if (restoredData.aspect_ratio) setAspectRatio(restoredData.aspect_ratio);
    if (restoredData.duration) setDuration(restoredData.duration);
    if (restoredData.sound !== undefined) setSound(restoredData.sound);
    if (restoredData.cfg_scale !== undefined) setCfgScale(restoredData.cfg_scale);
    if (restoredData.image_url) {
      setImageUrl(restoredData.image_url);
      setImagePreview(restoredData.image_url);
      const img = new Image();
      img.onload = () => setImageOrientation(img.height > img.width ? 'portrait' : 'landscape');
      img.src = restoredData.image_url;
    }
    if (restoredData.video_url) {
      setVideoUrl(restoredData.video_url);
      onVideoStateChange?.(false, restoredData.video_url, null, null);
    }
  }, [restoredData]);

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    const objUrl = URL.createObjectURL(file);
    setImagePreview(objUrl);
    setImageUrl(null);
    const img = new Image();
    img.onload = () => {
      const isPortrait = img.height > img.width;
      setImageOrientation(isPortrait ? 'portrait' : 'landscape');
      setAspectRatio(isPortrait ? '9:16' : '16:9');
    };
    img.src = objUrl;
  };

  const handleElementAdd = async (e) => {
    const file = e.target.files[0];
    if (!file || elements.length >= 3) return;
    const preview = URL.createObjectURL(file);
    // Upload immediately
    try {
      const fd = new FormData();
      fd.append('image', file);
      const { data } = await axios.post(`${API}/api/recipes/happyhorse-direct/upload-element`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setElements(prev => [...prev, { file, preview, url: data.url, label: `e${prev.length + 1}` }]);
    } catch {
      alert('Element upload failed');
    }
    if (elementInputRef.current) elementInputRef.current.value = '';
  };

  const uploadImage = async () => {
    if (!imageFile) return imageUrl;
    if (imageUrl) return imageUrl;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', imageFile);
      const { data } = await axios.post(`${API}/api/recipes/happyhorse-direct/upload-image`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        timeout: 30000,
      });
      setImageUrl(data.url);
      setUploading(false);
      return data.url;
    } catch {
      setUploading(false);
      throw new Error('Image upload failed');
    }
  };

  const handleGenerate = async () => {
    if (!imageFile && !imageUrl) return alert('Please upload a keyframe image');
    if (!prompt.trim()) return alert('Please enter a prompt');
    try {
      setGenerating(true);
      setVideoUrl(null);
      try { onVideoStateChange?.(true, null, null, null); } catch {}
      const url = await uploadImage();
      if (!url) { alert('Image upload returned no URL'); setGenerating(false); return; }
      try { onVideoStateChange?.(true, null, url, prompt.trim()); } catch {}
      const { data } = await axios.post(`${API}/api/recipes/happyhorse-direct/generate`, {
        image_url: url,
        prompt: prompt.trim(),
        aspect_ratio: aspectRatio,
        duration,
        sound,
        cfg_scale: cfgScale,
        elements: elements.filter(el => el.url).map(el => ({ image_url: el.url, label: el.label })),
      }, { headers: { Authorization: `Bearer ${token}` } });

      const result = await pollTask(data.task_id, token);
      if (result.video_url) {
        setVideoUrl(result.video_url);
        try { onVideoStateChange?.(false, result.video_url, null, null); } catch {}
        axios.get(`${API}/api/recipes/happyhorse-direct/runs`, {
          headers: { Authorization: `Bearer ${token}` },
        }).then(({ data: d }) => setRuns(d.runs || [])).catch(() => {});
      }
    } catch (err) {
      alert(err.message || 'Generation failed');
      try { onVideoStateChange?.(false, null, null, null); } catch {}
    } finally {
      setGenerating(false);
    }
  };

  const loadRun = (run) => {
    if (run.inputs) {
      setPrompt(run.inputs.prompt || '');
      setAspectRatio(run.inputs.aspect_ratio || '16:9');
      setDuration(run.inputs.duration || 5);
      setSound(run.inputs.sound || false);
      setCfgScale(run.inputs.cfg_scale ?? 0.5);
      if (run.inputs.image_url) {
        setImageUrl(run.inputs.image_url);
        setImagePreview(run.inputs.image_url);
        setImageFile(null);
        const img = new Image();
        img.onload = () => setImageOrientation(img.height > img.width ? 'portrait' : 'landscape');
        img.src = run.inputs.image_url;
      }
    }
    const videoOut = run.outputs?.find(o => o.type === 'video' && o.url);
    if (videoOut) {
      setVideoUrl(videoOut.url);
      setGenerating(false);
      onVideoStateChange?.(false, videoOut.url, null, null);
    }
  };

  const timeAgo = (iso) => {
    if (!iso) return '';
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <div className="space-y-4" data-testid="happyhorse-direct-inputs">
      {/* Keyframe Upload */}
      <div>
        <label className="text-[10px] text-white/40 uppercase tracking-wider block mb-1.5">Keyframe Image</label>
        <input type="file" ref={fileInputRef} accept="image/*" className="hidden" onChange={handleImageSelect} />
        {imagePreview ? (
          <div className={`relative group rounded-xl overflow-hidden ${imageOrientation === 'portrait' ? 'max-w-[60%] mx-auto' : 'w-full'}`} style={{ border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(0,0,0,0.3)' }}>
            <img src={imagePreview} alt="Keyframe" className="w-full object-contain" style={{ maxHeight: imageOrientation === 'portrait' ? '280px' : '140px' }} />
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
              <button onClick={() => fileInputRef.current?.click()} className="px-3 py-1.5 rounded-lg text-[10px] font-medium text-white bg-white/20 hover:bg-white/30 backdrop-blur-sm" data-testid="change-keyframe-btn">Change</button>
            </div>
            {uploading && (
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                <Loader2 className="w-5 h-5 text-amber-400 animate-spin" />
              </div>
            )}
          </div>
        ) : (
          <button onClick={() => fileInputRef.current?.click()} className="w-full py-8 rounded-xl flex flex-col items-center justify-center gap-2 transition-all hover:bg-white/[0.04]" style={{ border: '1px dashed rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.02)' }} data-testid="upload-keyframe-btn">
            <Upload className="w-5 h-5 text-white/30" />
            <span className="text-[11px] text-white/40">Upload keyframe image</span>
          </button>
        )}
      </div>

      {/* Prompt */}
      <div>
        <label className="text-[10px] text-white/40 uppercase tracking-wider block mb-1.5">Prompt</label>
        <textarea value={prompt} onChange={e => setPrompt(e.target.value)} placeholder="Describe the motion and action..." rows={4} className="w-full px-3 py-2.5 rounded-xl text-xs text-white/80 placeholder-white/20 bg-white/[0.03] border border-white/[0.08] focus:outline-none focus:border-amber-500/30 resize-none transition-colors" data-testid="hh-prompt-input" />
      </div>

      {/* Aspect Ratio & Duration */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] text-white/40 uppercase tracking-wider block mb-1.5">Aspect Ratio</label>
          <div className="flex gap-1.5">
            {['16:9', '9:16'].map(ar => (
              <button key={ar} onClick={() => setAspectRatio(ar)} className={`flex-1 py-2 rounded-lg text-[11px] font-medium transition-all ${aspectRatio === ar ? 'bg-amber-500/15 border-amber-500/40 text-amber-300' : 'bg-white/[0.03] border-white/[0.08] text-white/50 hover:bg-white/[0.06]'}`} style={{ border: '1px solid' }} data-testid={`hh-ar-${ar.replace(':', '-')}-btn`}>
                {ar === '16:9' ? '16:9' : '9:16'}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-[10px] text-white/40 uppercase tracking-wider block mb-1.5">Duration: {duration}s</label>
          <input type="range" min={3} max={15} step={1} value={duration} onChange={e => setDuration(Number(e.target.value))} className="w-full h-1.5 rounded-full appearance-none cursor-pointer" style={{ background: `linear-gradient(to right, #F59E0B ${((duration - 3) / 12) * 100}%, rgba(255,255,255,0.08) ${((duration - 3) / 12) * 100}%)` }} data-testid="hh-duration-slider" />
          <div className="flex justify-between text-[8px] text-white/20 mt-0.5"><span>3s</span><span>15s</span></div>
        </div>
      </div>

      {/* Sound & CFG Scale */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] text-white/40 uppercase tracking-wider block mb-1.5">Sound</label>
          <button onClick={() => setSound(!sound)} className={`w-full py-2 rounded-lg text-[11px] font-medium transition-all flex items-center justify-center gap-1.5 ${sound ? 'bg-amber-500/15 border-amber-500/40 text-amber-300' : 'bg-white/[0.03] border-white/[0.08] text-white/50'}`} style={{ border: '1px solid' }} data-testid="hh-sound-toggle">
            {sound ? <><Volume2 className="w-3 h-3" /> Audio ON</> : <><VolumeX className="w-3 h-3" /> Audio OFF</>}
          </button>
        </div>
        <div>
          <label className="text-[10px] text-white/40 uppercase tracking-wider block mb-1.5">Creativity: {(cfgScale * 100).toFixed(0)}%</label>
          <input type="range" min={0} max={1} step={0.05} value={cfgScale} onChange={e => setCfgScale(Number(e.target.value))} className="w-full h-1.5 rounded-full appearance-none cursor-pointer" style={{ background: `linear-gradient(to right, #F59E0B ${cfgScale * 100}%, rgba(255,255,255,0.08) ${cfgScale * 100}%)` }} data-testid="hh-cfg-slider" />
          <div className="flex justify-between text-[8px] text-white/20 mt-0.5"><span>Strict</span><span>Creative</span></div>
        </div>
      </div>

      {/* Elements (up to 3 character/object references) */}
      <div>
        <label className="text-[10px] text-white/40 uppercase tracking-wider block mb-1.5">Elements — Character/Object References (max 3)</label>
        <input type="file" ref={elementInputRef} accept="image/*" className="hidden" onChange={handleElementAdd} />
        <div className="flex gap-2 flex-wrap">
          {elements.map((el, i) => (
            <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
              <img src={el.preview} alt={el.label} className="w-full h-full object-cover" />
              <button onClick={() => setElements(prev => prev.filter((_, j) => j !== i))} className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-black/60 flex items-center justify-center" data-testid={`remove-element-${i}`}>
                <X className="w-2.5 h-2.5 text-white/60" />
              </button>
              <span className="absolute bottom-0 left-0 right-0 text-[7px] text-center py-0.5 bg-black/60 text-white/50">E{i + 1}</span>
            </div>
          ))}
          {elements.length < 3 && (
            <button onClick={() => elementInputRef.current?.click()} className="w-16 h-16 rounded-lg flex flex-col items-center justify-center gap-1 transition-all hover:bg-white/[0.04]" style={{ border: '1px dashed rgba(255,255,255,0.1)' }} data-testid="add-element-btn">
              <Plus className="w-3.5 h-3.5 text-white/25" />
              <span className="text-[7px] text-white/20">Add</span>
            </button>
          )}
        </div>
      </div>

      {/* Generate */}
      <button onClick={handleGenerate} disabled={generating || (!imageFile && !imageUrl) || !prompt.trim()} className="w-full py-3 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-40 flex items-center justify-center gap-2" style={{ background: generating ? 'rgba(245,158,11,0.12)' : 'linear-gradient(135deg, #F59E0B, #D97706)', boxShadow: generating ? 'none' : '0 4px 16px rgba(245,158,11,0.25)' }} data-testid="hh-generate-btn">
        {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating {duration}s Video...</> : <><Play className="w-4 h-4" /> Generate Video</>}
      </button>

      {/* Recent Runs */}
      {runs.length > 0 && (
        <div data-testid="hh-recent-runs">
          <button onClick={() => setRunsOpen(!runsOpen)} className="flex items-center justify-between w-full py-1.5 mb-1">
            <div className="flex items-center gap-1.5">
              <Clock className="w-3 h-3 text-white/30" />
              <span className="text-[10px] text-white/40 uppercase tracking-wider font-semibold">Recent Runs</span>
              <span className="text-[9px] text-white/20 bg-white/[0.05] px-1.5 py-0.5 rounded-full">{runs.length}</span>
            </div>
            <ChevronDown className={`w-3 h-3 text-white/20 transition-transform ${runsOpen ? 'rotate-180' : ''}`} />
          </button>
          {runsOpen && (
            <div className="space-y-1.5 max-h-64 overflow-y-auto">
              {runs.map(run => {
                const videoOut = run.outputs?.find(o => o.type === 'video' && o.url);
                const isActive = videoOut && videoUrl === videoOut.url;
                return (
                  <button key={run.run_id} onClick={() => loadRun(run)} className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-xl text-left transition-all ${isActive ? 'bg-amber-500/10 border-amber-500/20' : 'bg-white/[0.02] border-white/[0.06] hover:bg-white/[0.04]'}`} style={{ border: '1px solid' }} data-testid={`hh-run-${run.run_id}`}>
                    <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-black/40">
                      {run.inputs?.image_url ? <img src={run.inputs.image_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Film className="w-4 h-4 text-white/20" /></div>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] text-white/60 truncate">{run.inputs?.prompt || 'No prompt'}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={`text-[9px] font-medium ${run.status === 'completed' ? 'text-emerald-400/70' : run.status === 'failed' ? 'text-red-400/70' : 'text-amber-400/70'}`}>{run.status === 'completed' ? 'Done' : run.status === 'failed' ? 'Failed' : 'Processing'}</span>
                        <span className="text-[9px] text-white/20">{run.inputs?.duration || 5}s</span>
                        {run.inputs?.sound && <Volume2 className="w-2.5 h-2.5 text-amber-400/40" />}
                        <span className="text-[9px] text-white/15">{timeAgo(run.started_at)}</span>
                      </div>
                    </div>
                    {isActive && <Check className="w-3 h-3 text-amber-400 flex-shrink-0" />}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
